import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ForumIcon from '@mui/icons-material/Forum';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { COLORS } from '../theme/colors';
import { GradientButton, GradientText, GlowCard } from '../components/ui/SharedUI';
import PageLayout from '../components/layout/PageLayout';
import type { Feature, FeatureId } from '../types/content';

const FEATURE_ICONS: Record<FeatureId, JSX.Element> = {
  automatizacion: <AutoFixHighIcon sx={{ color: COLORS.purple[400], fontSize: 28 }} />,
  agentes: <SmartToyIcon sx={{ color: COLORS.purple[400], fontSize: 28 }} />,
  centralizacion: <ForumIcon sx={{ color: COLORS.purple[400], fontSize: 28 }} />,
};

// ── Hero ─────────────────────────────────────────────────────
function HeroSection(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: { xs: '78vh', md: '84vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: 'transparent',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 10, md: 6 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: {
                xs: '2rem',
                sm: '2.45rem',
                md: '3.15rem',
                lg: '3.45rem',
              },
              lineHeight: {
                xs: 1.16,
                md: 1.12,
              },
              fontWeight: 700,
              letterSpacing: '-0.035em',
              mb: 3,
              color: 'text.primary',
              maxWidth: 780,
              mx: 'auto',
            }}
          >
            {t('home.hero.headline1')}{' '}
            <GradientText>{t('home.hero.highlight')}</GradientText>
            {t('home.hero.headline2')}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.15rem' },
              color: 'text.secondary',
              mb: 5,
              maxWidth: 520,
              mx: 'auto',
            }}
          >
            {t('home.hero.subtext')}
          </Typography>

          <GradientButton onClick={() => navigate('/contacto')} sx={{ fontSize: '1rem', px: 5, py: 1.75 }}>
            {t('home.hero.cta')}
          </GradientButton>
        </Box>
      </Container>
    </Box>
  );
}

// ── Features ─────────────────────────────────────────────────
function FeaturesSection(): JSX.Element {
  const { t } = useTranslation();
  const features = t('home.features', { returnObjects: true }) as Feature[];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: 'transparent',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            mb: { xs: 6, md: 8 },
            fontSize: { xs: '1.75rem', md: '2.25rem' },
          }}
        >
          {t('home.futuro.title')}
        </Typography>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={feature.id}>
              <GlowCard sx={{ height: '100%', animationDelay: `${index * 0.1}s` }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    background: `${COLORS.purple[500]}22`,
                    border: `1px solid ${COLORS.purple[500]}44`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                  }}
                >
                  {FEATURE_ICONS[feature.id]}
                </Box>

                <Typography variant="h5" sx={{ mb: 1.5, fontSize: '1.15rem' }}>
                  {feature.title}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {feature.description}
                </Typography>
              </GlowCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

// ── Page ─────────────────────────────────────────────────────
export default function HomePage(): JSX.Element {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <PageLayout>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            ? `
              radial-gradient(circle at 50% 8%, rgba(168,85,247,0.24) 0%, rgba(147,51,234,0.14) 24%, transparent 48%),
              radial-gradient(circle at 18% 44%, rgba(147,51,234,0.16) 0%, rgba(147,51,234,0.08) 28%, transparent 52%),
              radial-gradient(circle at 82% 44%, rgba(191,0,255,0.14) 0%, rgba(147,51,234,0.07) 30%, transparent 56%),
              radial-gradient(circle at 50% 86%, rgba(168,85,247,0.13) 0%, rgba(147,51,234,0.06) 30%, transparent 58%),
              linear-gradient(180deg, ${COLORS.dark[900]} 0%, ${COLORS.dark[800]} 44%, ${COLORS.dark[900]} 100%)
            `
            : `
              radial-gradient(circle at 50% 8%, rgba(168,85,247,0.15) 0%, rgba(147,51,234,0.08) 24%, transparent 48%),
              radial-gradient(circle at 18% 44%, rgba(147,51,234,0.10) 0%, rgba(147,51,234,0.05) 28%, transparent 52%),
              radial-gradient(circle at 82% 44%, rgba(191,0,255,0.09) 0%, rgba(147,51,234,0.04) 30%, transparent 56%),
              linear-gradient(180deg, ${COLORS.light[100]} 0%, ${COLORS.gray[100]} 100%)
            `,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: 80, md: 40 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 420, md: 760 },
            height: { xs: 420, md: 760 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.26) 0%, rgba(147,51,234,0.12) 34%, transparent 68%)',
            filter: 'blur(58px)',
            opacity: isDark ? 0.72 : 0.34,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: '58%', md: '50%' },
            left: { xs: '-160px', md: '-90px' },
            width: { xs: 320, md: 520 },
            height: { xs: 320, md: 520 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(191,0,255,0.18) 0%, rgba(147,51,234,0.08) 38%, transparent 72%)',
            filter: 'blur(52px)',
            opacity: isDark ? 0.58 : 0.26,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: '64%', md: '55%' },
            right: { xs: '-170px', md: '-80px' },
            width: { xs: 340, md: 560 },
            height: { xs: 340, md: 560 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.17) 0%, rgba(147,51,234,0.08) 38%, transparent 74%)',
            filter: 'blur(56px)',
            opacity: isDark ? 0.54 : 0.24,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: { xs: 40, md: 120 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 360, md: 680 },
            height: { xs: 220, md: 360 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.16) 0%, rgba(147,51,234,0.07) 42%, transparent 76%)',
            filter: 'blur(64px)',
            opacity: isDark ? 0.46 : 0.22,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <HeroSection />
          <FeaturesSection />
        </Box>
      </Box>
    </PageLayout>
  );
}